/**
 * ELF-CIRCLE — Made In New Jersey (owned property hub).
 *
 * Section 7 of the company overview treats this as an ELF-CIRCLE-owned asset,
 * not a client case study, so it gets a hub of its own rather than a Portfolio
 * row: the statewide footprint, the 2027 edition, and a partner path that is
 * deliberately separate from "Book a consultation" — the buyer here is a county
 * arts council or a tourism board, not a brand marketing lead.
 *
 * Signature mechanism: the 21-county sweep. A pointer travels the grid on its
 * own so the footprint reads as statewide motion rather than a static list;
 * hovering pins a county and the sweep resumes when the pointer leaves. The
 * counter above it counts the counties already swept, so the "21" in the copy
 * is something the visitor watches arrive rather than something they're told.
 *
 * Static page — imagery is bundled rather than an editable CMS slot.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import badgeArt from '../../../../../assets/elf/minj-badge.jpg';
import proofArt from '../../../../../assets/elf/minj-proof-network.jpg';

interface County {
  name: string;
  /** Programming region, shown in the readout beside the county. */
  region: 'North' | 'Central' | 'Shore' | 'South';
  /** One line on what the county brings to a statewide programme. */
  note: string;
}

/**
 * All 21, in the sweep order the grid draws them — north to south, so the
 * pointer travels the state rather than jumping around an alphabetised list.
 */
const COUNTIES: County[] = [
  { name: 'Sussex', region: 'North', note: 'Rural arts corridor — outdoor stages.' },
  { name: 'Passaic', region: 'North', note: 'Paterson’s music and mural scene.' },
  { name: 'Bergen', region: 'North', note: 'Largest county by population.' },
  { name: 'Warren', region: 'North', note: 'River towns, festival-scale greens.' },
  { name: 'Morris', region: 'North', note: 'Historic theatre circuit.' },
  { name: 'Essex', region: 'North', note: 'NJPAC — the institutional anchor.' },
  { name: 'Hudson', region: 'North', note: 'Jersey City — the networking home room.' },
  { name: 'Hunterdon', region: 'Central', note: 'Studio and maker community.' },
  { name: 'Somerset', region: 'Central', note: 'Suburban amphitheatre reach.' },
  { name: 'Union', region: 'Central', note: 'Dense, transit-served audiences.' },
  { name: 'Middlesex', region: 'Central', note: 'University audience pipeline.' },
  { name: 'Mercer', region: 'Central', note: 'Trenton — the statewide policy room.' },
  { name: 'Monmouth', region: 'Shore', note: 'Asbury Park — live music legacy.' },
  { name: 'Ocean', region: 'Shore', note: 'Summer-season boardwalk reach.' },
  { name: 'Burlington', region: 'South', note: 'Bridge between Philly and the Shore.' },
  { name: 'Camden', region: 'South', note: 'Waterfront amphitheatre scale.' },
  { name: 'Gloucester', region: 'South', note: 'Emerging South Jersey venues.' },
  { name: 'Salem', region: 'South', note: 'Smallest county — the reach test.' },
  { name: 'Cumberland', region: 'South', note: 'Glass-town arts heritage.' },
  { name: 'Atlantic', region: 'Shore', note: 'Atlantic City — the 2027 edition.' },
  { name: 'Cape May', region: 'Shore', note: 'The southern terminus of the sweep.' },
];

/**
 * Target window for the next edition.
 *
 * The overview commits to "Atlantic City, NJ in 2027" without naming a day, so
 * this is a placeholder the client replaces once the date is booked — the strip
 * labels it "target window" rather than a confirmed date for exactly that
 * reason. Change this one constant and the countdown follows.
 */
const NEXT_EDITION = new Date('2027-06-12T16:00:00-04:00');

/** Sweep dwell per county, in ms. */
const SWEEP_MS = 900;
/** A tap pins a county; with no hover to leave, the pin lets go after this. */
const TOUCH_PIN_MS = 6000;

function daysUntil(target: Date): number {
  return Math.max(0, Math.ceil((target.getTime() - Date.now()) / 86_400_000));
}

export const MadeInNewJersey: React.FC = () => {
  const consult = useConsult();
  /** Index the sweep is on. Hovering pins a different one without stopping it. */
  const [swept, setSwept] = useState(0);
  const [pinned, setPinned] = useState<number | null>(null);
  const pausedRef = useRef(false);
  /** Mirror of `pinned` for event handlers, which would otherwise read a stale value. */
  const pinnedRef = useRef<number | null>(null);
  /** What pinned the county — only that same source releases it. */
  const pinSourceRef = useRef<'mouse' | 'touch' | 'keyboard' | null>(null);
  const releaseTimerRef = useRef<number>();
  /** Last pointer type, so a click can tell a tap from a mouse click. */
  const pointerRef = useRef('mouse');
  const [days] = useState(() => daysUntil(NEXT_EDITION));

  /* --- the statewide sweep ------------------------------------------------- */
  useEffect(() => {
    // Runs for reduced-motion visitors too. The sweep steps a highlight from
    // county to county — a state change, not movement — and skipping it left
    // iPhones with Reduce Motion on frozen at 21/21. Only the lift animation is
    // dropped for them, in CSS.
    const id = window.setInterval(() => {
      if (pausedRef.current) return;
      setSwept((i) => (i + 1) % COUNTIES.length);
    }, SWEEP_MS);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(releaseTimerRef.current);
    };
  }, []);

  const pin = (i: number, source: 'mouse' | 'touch' | 'keyboard') => {
    window.clearTimeout(releaseTimerRef.current);
    pinSourceRef.current = source;
    pinnedRef.current = i;
    pausedRef.current = true;
    setPinned(i);
    // Touch has no "leave", so a tapped county lets go on its own.
    if (source === 'touch') releaseTimerRef.current = window.setTimeout(release, TOUCH_PIN_MS);
  };

  /** Unpin, and carry the sweep on from the county that was pinned. */
  function release() {
    window.clearTimeout(releaseTimerRef.current);
    const was = pinnedRef.current;
    pinSourceRef.current = null;
    pinnedRef.current = null;
    pausedRef.current = false;
    if (was !== null) setSwept(was);
    setPinned(null);
  }

  const active = pinned ?? swept;
  const county = COUNTIES[active];
  // Position in the sweep. A pinned county shows its own number — it used to
  // read 21/21 whenever anything was pinned, which looked like a stuck counter.
  const reached = active + 1;

  const partner = (source: string, title: string) => () =>
    consult?.open({ source, interest: 'Sponsorship', title });

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          {/* --- owned-property band --- */}
          {/* The badge artwork is pale, so this band runs light: the nav keeps
              its solid bar and the copy is set in dark ink (.minj-band--light). */}
          <div className="minj-band minj-band--light elf-page__bleed">
            <img className="minj-band__art" src={badgeArt} alt="Made In New Jersey" />
            <div className="minj-band__inner elf-page__col">
              <ElfPageNav current="/made-in-new-jersey" />
              <div style={{ padding: '18px var(--pgut) 0' }}>
                <ElfCrumb
                  trail={[
                    { label: 'Home', to: '/' },
                    { label: 'Events', to: '/networking-events' },
                    'Made In New Jersey',
                  ]}
                />
              </div>
              <div className="minj-band__copy">
                <span className="eyebrow eyebrow--film">
                  <i />
                  Owned property — not client work
                </span>
                <h1 className="elf-page__h1 elf-page__h1--film">Made In New Jersey</h1>
                <p className="minj-band__lede">
                  A statewide, 21-county Arts &amp; Culture initiative, owned exclusively by
                  ELF-CIRCLE INC. and produced annually. The inaugural festival is already in the
                  ground; the next edition lands in Atlantic City.
                </p>
                <div className="minj-band__ctas">
                  <button
                    type="button"
                    className="cta cta--film mag"
                    onClick={partner('made-in-nj.partner', 'Become a Made In New Jersey partner')}
                  >
                    Become a partner
                  </button>
                  <Link className="pillghost pillghost--film" to={route('/sponsorship')}>
                    Sponsorship tiers →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* --- the 2027 strip --- */}
          <div className="minj-strip">
            <div className="minj-strip__lead">
              <span className="statl">Next edition — target window</span>
              <span className="minj-strip__where">Atlantic City, NJ · 2027</span>
            </div>
            <div className="minj-strip__nums">
              <div className="minj-num">
                <span className="statn">{days.toLocaleString()}</span>
                <span className="statl">Days out</span>
              </div>
              <div className="minj-num">
                <span className="statn">21</span>
                <span className="statl">Counties</span>
              </div>
              <div className="minj-num">
                <span className="statn">1×</span>
                <span className="statl">Annually, ongoing</span>
              </div>
            </div>
          </div>

          {/* --- signature: the 21-county sweep --- */}
          <div className="minj-sweep">
            <div className="minj-sweep__head">
              <span className="colhead">The footprint</span>
              <h2 className="elf-page__h2">Twenty-one counties, one programme</h2>
              <p className="wbody" style={{ maxWidth: '62ch' }}>
                Statewide is a claim most cultural properties make about a single city. This one is
                built county by county — arts councils, tourism boards and local venues as
                programming partners, not as an audience to be bought.
              </p>
            </div>

            <div className="minj-sweep__body">
              <div
                className="minj-grid"
                onPointerLeave={(e) => {
                  if (e.pointerType === 'mouse' && pinSourceRef.current === 'mouse') release();
                }}
              >
                {COUNTIES.map((c, i) => (
                  <button
                    key={c.name}
                    type="button"
                    className="minj-cty"
                    data-lit={i <= active ? 'true' : undefined}
                    data-on={i === active ? 'true' : undefined}
                    data-region={c.region}
                    onPointerDown={(e) => {
                      pointerRef.current = e.pointerType;
                    }}
                    onPointerEnter={(e) => {
                      if (e.pointerType === 'mouse') pin(i, 'mouse');
                    }}
                    onClick={() => {
                      // Touch has no hover: a tap pins the county, a second tap releases it.
                      if (pointerRef.current === 'mouse') return;
                      if (pinnedRef.current === i) release();
                      else pin(i, 'touch');
                    }}
                    onFocus={(e) => {
                      // Keyboard only. A tap also focuses the button on some phones,
                      // and pinning on that focus is what could lock a county in place.
                      let keyboard = false;
                      try {
                        keyboard = e.currentTarget.matches(':focus-visible');
                      } catch {
                        keyboard = false;
                      }
                      if (keyboard) pin(i, 'keyboard');
                    }}
                    onBlur={() => {
                      if (pinSourceRef.current === 'keyboard') release();
                    }}
                  >
                    <span className="minj-cty__n">{String(i + 1).padStart(2, '0')}</span>
                    <span className="minj-cty__name">{c.name}</span>
                  </button>
                ))}
              </div>

              <aside className="minj-read" aria-live="polite">
                <span className="minj-read__count">
                  {String(reached).padStart(2, '0')}
                  <i>/21</i>
                </span>
                <span className="statl">{county.region} Jersey</span>
                <span className="minj-read__name">{county.name}</span>
                <p className="wtag" style={{ fontSize: 13.5 }}>
                  {county.note}
                </p>
                <span className="minj-read__hint">
                  {pinned === null ? (
                    <>
                      Sweeping — <span className="hint-hover">hover</span>
                      <span className="hint-touch">tap</span> to pin a county
                    </>
                  ) : (
                    <>
                      Pinned — <span className="hint-hover">move away</span>
                      <span className="hint-touch">tap again or wait</span> to resume
                    </>
                  )}
                </span>
              </aside>
            </div>
          </div>

          {/* --- why it matters --- */}
          <div className="minj-proof">
            <span className="minj-proof__art">
              <img src={proofArt} alt="" />
              <span />
            </span>
            <div className="minj-proof__cards">
              {[
                ['Ownership, not promotion', 'ELF-CIRCLE hosted and produced the inaugural festival. This is a property the firm originates and runs end to end — not a client engagement it markets.'],
                ['Proprietary audience data', 'A 21-county initiative generates direct community relationships and first-party audience data no client engagement can hand back.'],
                ['Proof of the thesis', 'The clearest demonstration that the firm does not just market culture — it builds cultural infrastructure, then sells the method.'],
              ].map(([head, body]) => (
                <div className="wblock chap" key={head}>
                  <span className="wnum">■</span>
                  <span className="stagehead">{head}</span>
                  <span className="wbar" />
                  <p className="wbody" style={{ fontSize: 15 }}>
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* --- partner pipeline --- */}
          <div className="minj-partner">
            <div>
              <span className="colhead">A different buyer</span>
              <h2 className="elf-page__h2" style={{ marginTop: 10 }}>
                County arts councils. Tourism boards. Statewide sponsors.
              </h2>
              <p className="wbody" style={{ maxWidth: '58ch', marginTop: 14 }}>
                A 21-county initiative is a municipal and cultural conversation, not a brand
                marketing one — so it gets its own path rather than sharing the consultation queue.
                Partner enquiries route to the Made In New Jersey pipeline directly.
              </p>
            </div>
            <button
              type="button"
              className="nextlink"
              onClick={partner('made-in-nj.pipeline', 'Made In New Jersey — partner enquiry')}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Partner pipeline</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Start a partner conversation
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Councils, boards and statewide sponsors.
                </span>
              </div>
              <span className="arw">→</span>
            </button>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/sponsorship')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Next</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Sponsorship &amp; partners
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Two properties, two pipelines.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <Link className="nextlink" to={route('/networking-events')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">The other cadence</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Monthly networking
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  National, monthly, market by market.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default MadeInNewJersey;
